import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { KillSwitchState } from "@/types/module-kill-switch";
import {
  DisableReasonForm,
  EnableReasonForm,
  MAX_REASON_LENGTH,
  disableReasonSchema,
  enableReasonSchema,
} from "../utils/toggleReasonSchema";
import { ModuleEffectsList } from "./ModuleEffectsList";

interface ToggleModuleDialogProps {
  state: KillSwitchState | null;
  isPending: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (reason: string | null) => void;
}

export const ToggleModuleDialog: React.FC<ToggleModuleDialogProps> = ({
  state,
  isPending,
  serverError,
  onClose,
  onSubmit,
}) => {
  const open = state !== null;

  let body: React.ReactNode = null;
  if (state) {
    body = state.enabled ? (
      <DisableForm
        state={state}
        isPending={isPending}
        serverError={serverError}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    ) : (
      <EnableForm
        state={state}
        isPending={isPending}
        serverError={serverError}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        {body}
      </DialogContent>
    </Dialog>
  );
};

interface FormProps {
  state: KillSwitchState;
  isPending: boolean;
  serverError: string | null;
  onClose: () => void;
  onSubmit: (reason: string | null) => void;
}

const DisableForm: React.FC<FormProps> = ({
  state,
  isPending,
  serverError,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const moduleName = t(`modules.codes.${state.module}.label`);
  const moduleLower = t(`modules.codes.${state.module}.lowercase`);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<DisableReasonForm>({
    resolver: zodResolver(disableReasonSchema),
    mode: "onChange",
    defaultValues: { reason: "" },
  });

  useEffect(() => {
    reset({ reason: "" });
  }, [state.module, reset]);

  const reason = watch("reason") ?? "";
  const trimmedLength = reason.trim().length;
  const charCount = reason.length;

  const submit = handleSubmit((values) => {
    const trimmed = values.reason.trim();
    onSubmit(trimmed.length > 0 ? trimmed : null);
  });

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <DialogHeader>
        <div className="flex items-center gap-2 text-custom-destructive-dark">
          <AlertTriangle className="size-5" />
          <DialogTitle className="text-custom-destructive-dark">
            {t("modules.dialog.disable.title", { name: moduleName })}
          </DialogTitle>
        </div>
        <p className="text-sm text-foreground">
          {t("modules.dialog.disable.lead", { module: moduleLower })}
        </p>
      </DialogHeader>

      <div className="rounded-lg border border-custom-destructive-dark/30 bg-custom-destructive-dark/5 p-4">
        <h4 className="text-sm font-semibold text-custom-destructive-dark mb-3">
          {t("modules.dialog.disable.effectsHeading")}
        </h4>
        <ModuleEffectsList module={state.module} compact />
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="disable-reason"
          className="text-sm font-medium text-foreground"
        >
          {t("modules.dialog.disable.reasonLabel")}
        </label>
        <Textarea
          id="disable-reason"
          placeholder={t("modules.dialog.disable.reasonPlaceholder")}
          rows={3}
          maxLength={MAX_REASON_LENGTH}
          aria-invalid={errors.reason ? true : undefined}
          autoFocus
          {...register("reason")}
        />
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {t("modules.dialog.disable.reasonHelp")}
          </p>
          <span
            className={
              charCount > MAX_REASON_LENGTH
                ? "text-xs font-medium text-custom-destructive-dark shrink-0"
                : "text-xs text-muted-foreground shrink-0"
            }
          >
            {charCount}/{MAX_REASON_LENGTH}
          </span>
        </div>
        {errors.reason?.message && (
          <p className="text-xs text-custom-destructive-dark">
            {t(errors.reason.message)}
          </p>
        )}
      </div>

      {serverError && (
        <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
          {serverError}
        </div>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          variant="new_destructive"
          disabled={isPending || !isValid || trimmedLength === 0}
        >
          {isPending
            ? t("modules.dialog.disable.confirming")
            : t("modules.dialog.disable.confirm", { name: moduleName })}
        </Button>
      </DialogFooter>
    </form>
  );
};

const EnableForm: React.FC<FormProps> = ({
  state,
  isPending,
  serverError,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const moduleName = t(`modules.codes.${state.module}.label`);
  const moduleLower = t(`modules.codes.${state.module}.lowercase`);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<EnableReasonForm>({
    resolver: zodResolver(enableReasonSchema),
    mode: "onChange",
    defaultValues: { reason: "" },
  });

  useEffect(() => {
    reset({ reason: "" });
  }, [state.module, reset]);

  const reason = watch("reason") ?? "";
  const charCount = reason.length;

  const submit = handleSubmit((values) => {
    const trimmed = values.reason.trim();
    onSubmit(trimmed.length > 0 ? trimmed : null);
  });

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <DialogHeader>
        <DialogTitle>
          {t("modules.dialog.enable.title", { name: moduleName })}
        </DialogTitle>
        <p className="text-sm text-foreground">
          {t("modules.dialog.enable.lead", { module: moduleLower })}
        </p>
      </DialogHeader>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="enable-reason"
          className="text-sm font-medium text-foreground"
        >
          {t("modules.dialog.enable.reasonLabel")}
        </label>
        <Textarea
          id="enable-reason"
          placeholder={t("modules.dialog.enable.reasonPlaceholder")}
          rows={3}
          maxLength={MAX_REASON_LENGTH}
          aria-invalid={errors.reason ? true : undefined}
          autoFocus
          {...register("reason")}
        />
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {t("modules.dialog.enable.reasonHelp")}
          </p>
          <span
            className={
              charCount > MAX_REASON_LENGTH
                ? "text-xs font-medium text-custom-destructive-dark shrink-0"
                : "text-xs text-muted-foreground shrink-0"
            }
          >
            {charCount}/{MAX_REASON_LENGTH}
          </span>
        </div>
        {errors.reason?.message && (
          <p className="text-xs text-custom-destructive-dark">
            {t(errors.reason.message)}
          </p>
        )}
      </div>

      {serverError && (
        <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
          {serverError}
        </div>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("common.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isPending}>
          {isPending
            ? t("modules.dialog.enable.confirming")
            : t("modules.dialog.enable.confirm", { name: moduleName })}
        </Button>
      </DialogFooter>
    </form>
  );
};
